import { z } from "zod";
import { IdSchema, IsoDateTimeSchema, pageSchema } from "./common.js";
import { ContentPartSchema, TaskProfileSchema } from "./provider.js";

// ---------------------------------------------------------------------------
// Definition — design §8. JSON is the source of truth; published is immutable.
// ---------------------------------------------------------------------------

export const NodeTypeSchema = z.enum([
	"START",
	"AGENT_INTERVIEW",
	"AGENT_TASK",
	"USER_INPUT",
	"APPROVAL",
	"QUALITY_GATE",
	"COST_GATE",
	"CONDITION",
	"PARALLEL",
	"MERGE",
	"TOOL",
	"ARTIFACT",
	"WAIT",
	"SUB_WORKFLOW",
	"END",
]);
export type NodeType = z.infer<typeof NodeTypeSchema>;

export const RetryPolicySchema = z.object({
	maxAttempts: z.number().int().min(1).max(10).default(3),
	initialDelayMs: z.number().int().min(0).default(50),
	backoffMultiplier: z.number().min(1).default(2),
	maxDelayMs: z.number().int().min(0).default(2000),
	retryableErrorClasses: z.array(z.string()).default(["TRANSIENT_PROVIDER", "TRANSIENT_RUNTIME"]),
});
export type RetryPolicy = z.infer<typeof RetryPolicySchema>;

export const RegulationFindingSchema = z.object({
	id: z.string().min(1),
	severity: z.enum(["violation", "review"]),
	quote: z.string(),
	rule: z.string().min(1),
	fix: z.string().min(1),
});
export type RegulationFinding = z.infer<typeof RegulationFindingSchema>;

/** Same envelope as Agentry's deterministic check_report.json. */
export const RegulationCheckReportSchema = z.object({
	findings: z.array(RegulationFindingSchema),
});
export type RegulationCheckReport = z.infer<typeof RegulationCheckReportSchema>;

export const RegulationCheckConfigSchema = z.object({
	stageGate: z.enum(["BEFORE_S3", "BEFORE_S5"]),
	artifactVariable: z.string().min(1),
	rulesVariable: z.string().min(1),
	deterministicReportVariable: z.string().min(1),
});
export type RegulationCheckConfig = z.infer<typeof RegulationCheckConfigSchema>;

export const AbMeasurementConfigSchema = z.object({
	assetKey: z.literal("cf-web-analytics-agentry-stg"),
	windowStartedAtVariable: z.string().min(1),
	evaluatedAtVariable: z.string().min(1),
	/** Offline contract fixture. Omit in production so provider-credential performs acquisition. */
	fixtureEventsVariable: z.string().min(1).nullable().default(null),
});
export type AbMeasurementConfig = z.infer<typeof AbMeasurementConfigSchema>;

export const LpProposalGenerationConfigSchema = z
	.object({
		proposalSetOutputVariable: z.string().min(1),
		pack: z.literal("agents/lp-agentry-self@0.1.0"),
	})
	.strict();
export type LpProposalGenerationConfig = z.infer<typeof LpProposalGenerationConfigSchema>;

export const LpResearchGenerationConfigSchema = z
	.object({
		pack: z.literal("agents/lp-agentry-self@0.1.0"),
		outputVariable: z.string().min(1),
		allowWeb: z.literal(false),
		maxRepairAttempts: z.literal(2),
	})
	.strict();
export type LpResearchGenerationConfig = z.infer<typeof LpResearchGenerationConfigSchema>;

export const LpImprovementGenerationConfigSchema = z
	.object({
		pack: z.literal("agents/lp-agentry-self@0.1.0"),
		outputVariable: z.string().min(1),
	})
	.strict();

/** Workspace deliverable modalities (B210). Multiple may apply (AND). */
export const DeliverableModalitySchema = z.enum([
	"text_packaging",
	"research_web",
	"image_visual",
	"file_office",
	"coding_repo",
	"runtime_preview",
]);
export type DeliverableModality = z.infer<typeof DeliverableModalitySchema>;

export const WorkflowNodeDefinitionSchema = z
	.object({
		nodeId: z.string().min(1),
		nodeType: NodeTypeSchema,
		stageId: z.string().min(1),
		displayName: z.string().min(1),
		next: z.array(z.string()).default([]),
		taskProfile: TaskProfileSchema.nullable().default(null),
		/** Prompt template for AGENT_* nodes. `{{key}}` is filled from variables. */
		promptTemplate: z.string().nullable().default(null),
		/** ARTIFACT nodes declare what they emit. */
		artifactType: z.string().nullable().default(null),
		artifactMimeType: z.string().nullable().default(null),
		artifactSourceVariable: z.string().nullable().default(null),
		gateType: z.enum(["APPROVAL", "QUALITY", "COST", "POLICY"]).nullable().default(null),
		/** Variable the node writes its output into. */
		outputVariable: z.string().nullable().default(null),
		/** Required nodes cannot be removed or weakened by vertical/custom layers. */
		required: z.boolean().default(true),
		retryPolicy: RetryPolicySchema.prefault({}),
		attemptTimeoutMs: z.number().int().min(100).default(60_000),
		/** Declared effects are written to the Effect Ledger before execution. */
		effectName: z.string().nullable().default(null),
		isReversible: z.boolean().default(true),
		regulationCheck: RegulationCheckConfigSchema.nullable().default(null),
		abMeasurement: AbMeasurementConfigSchema.nullable().default(null),
		proposalGeneration: LpProposalGenerationConfigSchema.nullable().default(null),
		researchGeneration: LpResearchGenerationConfigSchema.nullable().default(null),
		improvementGeneration: LpImprovementGenerationConfigSchema.nullable().default(null),
		/** Modality this node serves (B210). Used with modalityOptional for skip. */
		modality: DeliverableModalitySchema.nullable().default(null),
		/** Skip when `required_modalities` does not include `modality`. */
		modalityOptional: z.boolean().default(false),
		/** QUALITY_GATE: modalities that must have mechanical evidence before success. */
		modalityPostconditions: z.array(DeliverableModalitySchema).default([]),
	})
	.superRefine((value, ctx) => {
		if (value.regulationCheck !== null && value.nodeType !== "SUB_WORKFLOW") {
			ctx.addIssue({
				code: "custom",
				path: ["regulationCheck"],
				message: "regulationCheck is only valid for SUB_WORKFLOW nodes",
			});
		}
		if (value.abMeasurement !== null && value.nodeType !== "SUB_WORKFLOW") {
			ctx.addIssue({
				code: "custom",
				path: ["abMeasurement"],
				message: "abMeasurement is only valid for SUB_WORKFLOW nodes",
			});
		}
		if (value.abMeasurement !== null && value.regulationCheck !== null) {
			ctx.addIssue({
				code: "custom",
				path: ["abMeasurement"],
				message: "SUB_WORKFLOW configuration must be unambiguous",
			});
		}
		if (value.proposalGeneration !== null && value.nodeType !== "SUB_WORKFLOW") {
			ctx.addIssue({
				code: "custom",
				path: ["proposalGeneration"],
				message: "proposalGeneration is only valid for SUB_WORKFLOW nodes",
			});
		}
		if (
			value.proposalGeneration !== null &&
			(value.abMeasurement !== null || value.regulationCheck !== null)
		) {
			ctx.addIssue({
				code: "custom",
				path: ["proposalGeneration"],
				message: "SUB_WORKFLOW configuration must be unambiguous",
			});
		}
		if (value.researchGeneration !== null && value.nodeType !== "SUB_WORKFLOW") {
			ctx.addIssue({
				code: "custom",
				path: ["researchGeneration"],
				message: "researchGeneration is only valid for SUB_WORKFLOW nodes",
			});
		}
		if (
			value.researchGeneration !== null &&
			(value.proposalGeneration !== null ||
				value.abMeasurement !== null ||
				value.regulationCheck !== null)
		) {
			ctx.addIssue({
				code: "custom",
				path: ["researchGeneration"],
				message: "SUB_WORKFLOW configuration must be unambiguous",
			});
		}
		if (value.improvementGeneration !== null && value.nodeType !== "SUB_WORKFLOW") {
			ctx.addIssue({
				code: "custom",
				path: ["improvementGeneration"],
				message: "improvementGeneration is only valid for SUB_WORKFLOW nodes",
			});
		}
		if (
			value.improvementGeneration !== null &&
			(value.researchGeneration !== null ||
				value.proposalGeneration !== null ||
				value.abMeasurement !== null ||
				value.regulationCheck !== null)
		) {
			ctx.addIssue({
				code: "custom",
				path: ["improvementGeneration"],
				message: "SUB_WORKFLOW configuration must be unambiguous",
			});
		}
	});
export type WorkflowNodeDefinition = z.infer<typeof WorkflowNodeDefinitionSchema>;

export const WorkflowStageDefinitionSchema = z.object({
	stageId: z.string().min(1),
	displayName: z.string().min(1),
	sequence: z.number().int().min(0),
});
export type WorkflowStageDefinition = z.infer<typeof WorkflowStageDefinitionSchema>;

export const WorkflowDefinitionSchema = z.object({
	id: IdSchema,
	key: z.string().min(1),
	version: z.number().int().min(1),
	displayName: z.string().min(1),
	status: z.enum(["DRAFT", "PUBLISHED", "DEPRECATED"]),
	stages: z.array(WorkflowStageDefinitionSchema),
	nodes: z.array(WorkflowNodeDefinitionSchema),
	workflowDeadlineMs: z.number().int().min(1000).default(3_600_000),
	definitionHash: z.string(),
	createdAt: IsoDateTimeSchema,
	publishedAt: IsoDateTimeSchema.nullable(),
});
export type WorkflowDefinition = z.infer<typeof WorkflowDefinitionSchema>;

// ---------------------------------------------------------------------------
// Execution state — design §20.3-20.4
// ---------------------------------------------------------------------------

export const Sha256HexSchema = z.string().regex(/^[0-9a-f]{64}$/);
export const WorkflowLayerProvenanceSchema = z.object({
	version: z.string().min(1),
	sha256: Sha256HexSchema,
});
export type WorkflowLayerProvenance = z.infer<typeof WorkflowLayerProvenanceSchema>;
export const EffectiveWorkflowProvenanceSchema = z.object({
	common: WorkflowLayerProvenanceSchema,
	vertical: WorkflowLayerProvenanceSchema.nullable(),
	custom: WorkflowLayerProvenanceSchema.nullable(),
});
export type EffectiveWorkflowProvenance = z.infer<typeof EffectiveWorkflowProvenanceSchema>;

export const ExecutionStatusSchema = z.enum([
	"CREATED",
	"QUEUED",
	"RUNNING",
	"WAITING",
	"PAUSE_REQUESTED",
	"SUSPENDED",
	"CANCEL_REQUESTED",
	"COMPENSATING",
	"SUCCEEDED",
	"FAILED",
	"CANCELLED",
	"TIMED_OUT",
]);
export type ExecutionStatus = z.infer<typeof ExecutionStatusSchema>;

export const TERMINAL_EXECUTION_STATUSES = [
	"SUCCEEDED",
	"FAILED",
	"CANCELLED",
	"TIMED_OUT",
] as const satisfies readonly ExecutionStatus[];

export function isTerminalExecutionStatus(status: ExecutionStatus): boolean {
	return (TERMINAL_EXECUTION_STATUSES as readonly string[]).includes(status);
}

export const WaitReasonSchema = z.enum([
	"USER_INPUT",
	"USER_APPROVAL",
	"MANUAL_REVIEW",
	"QUALITY_REMEDIATION",
	"COST_APPROVAL",
	"TIMER",
	"SCHEDULE",
	"EXTERNAL_EVENT",
	"DEPENDENCY",
	"SUBWORKFLOW",
	"CREDENTIAL_REFRESH",
	"CREDENTIAL_CAPACITY",
	"QUOTA_RESET",
	"RUNTIME_CAPACITY",
	"GIT_CONFLICT_RESOLUTION",
	"ADMIN_ACTION",
	"PROVIDER_FALLBACK_APPROVAL",
]);
export type WaitReason = z.infer<typeof WaitReasonSchema>;

export const FailureClassSchema = z.enum([
	"TRANSIENT_PROVIDER",
	"TRANSIENT_RUNTIME",
	"CREDENTIAL_REQUIRED",
	"QUOTA_EXHAUSTED",
	"CONTRACT_VIOLATION",
	"QUALITY_REJECTED",
	"POLICY_DENIED",
	"DATA_CONFLICT",
	"USER_REJECTED",
	"PERMANENT_EXTERNAL",
	"ENGINE_ERROR",
]);
export type FailureClass = z.infer<typeof FailureClassSchema>;

export const NodeStatusSchema = z.enum([
	"PENDING",
	"READY",
	"QUEUED",
	"RUNNING",
	"WAITING",
	"RETRY_SCHEDULED",
	"SUCCEEDED",
	"FAILED",
	"SKIPPED",
	"CANCELLED",
	"TIMED_OUT",
	"COMPENSATING",
	"COMPENSATED",
	"COMPENSATION_FAILED",
]);
export type NodeStatus = z.infer<typeof NodeStatusSchema>;

export const AttemptStatusSchema = z.enum([
	"STARTING",
	"RUNNING",
	"SUCCEEDED",
	"FAILED",
	"TIMED_OUT",
	"CANCELLED",
	"LOST",
]);
export type AttemptStatus = z.infer<typeof AttemptStatusSchema>;

export const StageStatusSchema = z.enum([
	"PENDING",
	"RUNNING",
	"WAITING",
	"SUCCEEDED",
	"FAILED",
	"SKIPPED",
	"CANCELLED",
	"TIMED_OUT",
]);
export type StageStatus = z.infer<typeof StageStatusSchema>;

export const WorkflowExecutionSchema = z.object({
	id: IdSchema,
	tenantId: IdSchema,
	projectId: IdSchema,
	sessionId: IdSchema,
	ucsId: IdSchema,
	workflowDefinitionId: IdSchema,
	workflowVersion: z.number().int().min(1),
	/** Definition + policy pinned at start. Design §20.19 invariant 1. */
	snapshotHash: z.string(),
	effectiveWorkflowHash: Sha256HexSchema.nullable(),
	effectiveWorkflowProvenance: EffectiveWorkflowProvenanceSchema.nullable(),
	rootExecutionId: IdSchema,
	parentExecutionId: IdSchema.nullable(),
	resumeFromCheckpointId: IdSchema.nullable(),
	replayOfExecutionId: IdSchema.nullable(),
	status: ExecutionStatusSchema,
	waitReason: WaitReasonSchema.nullable(),
	failureClass: FailureClassSchema.nullable(),
	failureDetail: z.string().nullable(),
	triggerType: z.enum(["USER", "SCHEDULE", "EVENT", "ADMIN"]),
	idempotencyKey: z.string(),
	stateVersion: z.number().int().min(0),
	startedByUserId: IdSchema,
	ownerUserId: IdSchema,
	startedAt: IsoDateTimeSchema.nullable(),
	deadlineAt: IsoDateTimeSchema.nullable(),
	endedAt: IsoDateTimeSchema.nullable(),
	currentCheckpointId: IdSchema.nullable(),
	estimatedCost: z.number().min(0),
	committedCost: z.number().min(0),
	currency: z.string(),
	createdAt: IsoDateTimeSchema,
	updatedAt: IsoDateTimeSchema,
});
export type WorkflowExecution = z.infer<typeof WorkflowExecutionSchema>;

export const StageExecutionSchema = z.object({
	id: IdSchema,
	executionId: IdSchema,
	stageId: z.string(),
	displayName: z.string(),
	sequence: z.number().int().min(0),
	status: StageStatusSchema,
	requiredNodeCount: z.number().int().min(0),
	completedNodeCount: z.number().int().min(0),
	startedAt: IsoDateTimeSchema.nullable(),
	endedAt: IsoDateTimeSchema.nullable(),
});
export type StageExecution = z.infer<typeof StageExecutionSchema>;

export const NodeExecutionSchema = z.object({
	id: IdSchema,
	executionId: IdSchema,
	stageExecutionId: IdSchema,
	nodeId: z.string(),
	nodeType: NodeTypeSchema,
	displayName: z.string(),
	/** node_id + branch_path + iteration. Design §20.4. */
	nodeInstanceKey: z.string(),
	branchPath: z.string(),
	iteration: z.number().int().min(0),
	status: NodeStatusSchema,
	waitReason: WaitReasonSchema.nullable(),
	inputRef: z.string().nullable(),
	outputRef: z.string().nullable(),
	outputHash: z.string().nullable(),
	currentAttemptNo: z.number().int().min(0),
	stateVersion: z.number().int().min(0),
	failureClass: FailureClassSchema.nullable(),
	startedAt: IsoDateTimeSchema.nullable(),
	endedAt: IsoDateTimeSchema.nullable(),
});
export type NodeExecution = z.infer<typeof NodeExecutionSchema>;

export const NodeAttemptSchema = z.object({
	id: IdSchema,
	nodeExecutionId: IdSchema,
	executionId: IdSchema,
	attemptNo: z.number().int().min(1),
	status: AttemptStatusSchema,
	workerId: z.string(),
	idempotencyKey: z.string(),
	providerInvocationId: IdSchema.nullable(),
	errorClass: FailureClassSchema.nullable(),
	errorDetail: z.string().nullable(),
	heartbeatAt: IsoDateTimeSchema.nullable(),
	startedAt: IsoDateTimeSchema,
	endedAt: IsoDateTimeSchema.nullable(),
});
export type NodeAttempt = z.infer<typeof NodeAttemptSchema>;

// ---------------------------------------------------------------------------
// Dynamic subtask — design §20.12
// ---------------------------------------------------------------------------

export const DynamicSubtaskSchema = z.object({
	id: IdSchema,
	executionId: IdSchema,
	nodeExecutionId: IdSchema,
	agentExecutionId: IdSchema,
	parentSubtaskId: IdSchema.nullable(),
	objective: z.string(),
	status: z.enum(["PLANNED", "RUNNING", "SUCCEEDED", "FAILED", "CANCELLED", "REPLANNED"]),
	assignedTo: z.string(),
	inputRef: z.string().nullable(),
	outputRef: z.string().nullable(),
	createdReason: z.string(),
	sequence: z.number().int().min(0),
	createdAt: IsoDateTimeSchema,
	endedAt: IsoDateTimeSchema.nullable(),
});
export type DynamicSubtask = z.infer<typeof DynamicSubtaskSchema>;

// ---------------------------------------------------------------------------
// Gate / Wait / Transition / Checkpoint / Effect
// ---------------------------------------------------------------------------

export const GateEvaluationSchema = z.object({
	id: IdSchema,
	executionId: IdSchema,
	nodeExecutionId: IdSchema,
	gateType: z.enum(["APPROVAL", "QUALITY", "COST", "POLICY"]),
	status: z.enum(["PENDING", "EVALUATING", "PASSED", "FAILED", "EXPIRED", "CANCELLED"]),
	result: z.string().nullable(),
	score: z.number().nullable(),
	threshold: z.number().nullable(),
	policyVersion: z.string(),
	decidedByType: z.string().nullable(),
	decidedById: IdSchema.nullable(),
	decidedAt: IsoDateTimeSchema.nullable(),
	expiresAt: IsoDateTimeSchema.nullable(),
	createdAt: IsoDateTimeSchema,
});
export type GateEvaluation = z.infer<typeof GateEvaluationSchema>;

export const WaitHandleSchema = z.object({
	id: IdSchema,
	executionId: IdSchema,
	nodeExecutionId: IdSchema,
	waitType: WaitReasonSchema,
	status: z.enum(["OPEN", "RESOLVED", "EXPIRED", "CANCELLED"]),
	expectedEventType: z.string().nullable(),
	correlationKey: z.string().nullable(),
	expiresAt: IsoDateTimeSchema.nullable(),
	resolvedAt: IsoDateTimeSchema.nullable(),
	resolutionRef: z.string().nullable(),
	createdAt: IsoDateTimeSchema,
});
export type WaitHandle = z.infer<typeof WaitHandleSchema>;

/** The resume token is returned once at creation; only its hash is stored. */
export const WaitHandleWithTokenSchema = WaitHandleSchema.extend({
	resumeToken: z.string(),
});
export type WaitHandleWithToken = z.infer<typeof WaitHandleWithTokenSchema>;

export const DesignApprovalSiteIdSchema = z.enum(["default", "lp", "newbiz", "creator"]);
export const DesignApprovalPayloadSchema = z
	.object({
		selectedArtifactId: IdSchema.optional(),
		selectedArtifactVersion: z.number().int().min(1).optional(),
		siteId: DesignApprovalSiteIdSchema,
		instructions: z.string(),
	})
	.refine(
		(value) =>
			(value.selectedArtifactId === undefined) === (value.selectedArtifactVersion === undefined),
		{ message: "selectedArtifactId and selectedArtifactVersion must be supplied together" },
	);
export type DesignApprovalPayload = z.infer<typeof DesignApprovalPayloadSchema>;

export const ResolveWaitHandleRequestSchema = z.object({
	resumeToken: z.string().min(1),
	decision: z.enum(["APPROVED", "REJECTED", "CHANGES_REQUESTED", "SUBMITTED"]),
	comment: z.string().max(2000).default(""),
	payload: z.record(z.string(), z.unknown()).default({}),
});
export type ResolveWaitHandleRequest = z.infer<typeof ResolveWaitHandleRequestSchema>;

export const ExecutionTransitionSchema = z.object({
	id: IdSchema,
	executionId: IdSchema,
	sequence: z.number().int().min(0),
	entityType: z.enum([
		"EXECUTION",
		"STAGE",
		"NODE",
		"ATTEMPT",
		"GATE",
		"WAIT",
		"EFFECT",
		"SUBTASK",
		"CHECKPOINT",
	]),
	entityId: IdSchema,
	fromStatus: z.string().nullable(),
	toStatus: z.string(),
	reasonCode: z.string(),
	actorType: z.string(),
	actorId: IdSchema.nullable(),
	correlationId: z.string(),
	traceId: z.string(),
	metadata: z.record(z.string(), z.unknown()),
	occurredAt: IsoDateTimeSchema,
});
export type ExecutionTransition = z.infer<typeof ExecutionTransitionSchema>;

export const CheckpointSchema = z.object({
	id: IdSchema,
	executionId: IdSchema,
	sequenceNo: z.number().int().min(0),
	checkpointType: z.enum([
		"STAGE_COMPLETE",
		"BEFORE_WAIT",
		"AFTER_EFFECT",
		"ARTIFACT_COMMITTED",
		"MANUAL",
	]),
	/** Completed node instance keys — the graph cursor. */
	completedNodeKeys: z.array(z.string()),
	variableState: z.record(z.string(), z.unknown()),
	artifactRefs: z.array(IdSchema),
	effectWatermark: z.number().int().min(0),
	snapshotHash: z.string(),
	committedCost: z.number().min(0),
	createdAt: IsoDateTimeSchema,
});
export type Checkpoint = z.infer<typeof CheckpointSchema>;

export const EffectRecordSchema = z.object({
	id: IdSchema,
	executionId: IdSchema,
	nodeExecutionId: IdSchema,
	effectType: z.string(),
	businessObjectKey: z.string(),
	idempotencyKey: z.string(),
	status: z.enum([
		"PREPARED",
		"EXECUTING",
		"COMMITTED",
		"FAILED",
		"COMPENSATING",
		"COMPENSATED",
		"UNKNOWN",
	]),
	isReversible: z.boolean(),
	externalReference: z.string().nullable(),
	createdAt: IsoDateTimeSchema,
	committedAt: IsoDateTimeSchema.nullable(),
});
export type EffectRecord = z.infer<typeof EffectRecordSchema>;

// ---------------------------------------------------------------------------
// Commands
// ---------------------------------------------------------------------------

export const SimulateOptionsSchema = z.object({
	unavailableProviderKeys: z.array(z.string()).default([]),
	/** Node ids that fail on their first attempt (retry then succeeds). */
	failNodeIdsOnce: z.array(z.string()).default([]),
	/** Node ids that fail on every attempt. */
	failNodeIdsAlways: z.array(z.string()).default([]),
	forceFailureClass: FailureClassSchema.nullable().default(null),
});
export type SimulateOptions = z.infer<typeof SimulateOptionsSchema>;

export const ImprovementExecutionConfigSchema = z
	.object({
		parentExecutionId: IdSchema,
		iteration: z.number().int().min(1),
		metrics: z.object({
			id: IdSchema,
			version: z.number().int().positive(),
			hash: Sha256HexSchema,
		}),
		abResult: z.object({
			id: IdSchema,
			version: z.number().int().positive(),
			hash: Sha256HexSchema,
		}),
		estimatedLlmMinutes: z.number().min(0),
		usedLlmMinutes: z.number().min(0).default(0),
		improveTotalMinutes: z.number().positive().default(60),
		fixture: z.boolean().default(false),
		scheduledAt: IsoDateTimeSchema,
	})
	.strict();
export type ImprovementExecutionConfig = z.infer<typeof ImprovementExecutionConfigSchema>;

export const StartExecutionRequestSchema = z
	.object({
		sessionId: IdSchema,
		/** Answers collected by the guided interview, keyed by question key. */
		interviewAnswers: z.record(z.string(), z.string()).default({}),
		triggerType: z.enum(["USER", "SCHEDULE", "EVENT", "ADMIN"]).default("USER"),
		externalTriggerId: z.string().nullable().default(null),
		simulate: SimulateOptionsSchema.prefault({}),
		effectiveWorkflowHash: Sha256HexSchema.optional(),
		effectiveWorkflowProvenance: EffectiveWorkflowProvenanceSchema.optional(),
		improvement: ImprovementExecutionConfigSchema.optional(),
	})
	.superRefine((value, ctx) => {
		if (
			(value.effectiveWorkflowHash === undefined) !==
			(value.effectiveWorkflowProvenance === undefined)
		) {
			ctx.addIssue({
				code: "custom",
				message: "effectiveWorkflowHash and effectiveWorkflowProvenance must be supplied together",
				path: [
					value.effectiveWorkflowHash === undefined
						? "effectiveWorkflowHash"
						: "effectiveWorkflowProvenance",
				],
			});
		}
	});
export type StartExecutionRequest = z.infer<typeof StartExecutionRequestSchema>;

export const ExecutionCommandRequestSchema = z.object({
	reason: z.string().min(1).max(500),
	expectedStateVersion: z.number().int().min(0).nullable().default(null),
});
export type ExecutionCommandRequest = z.infer<typeof ExecutionCommandRequestSchema>;

export const RetryNodeRequestSchema = ExecutionCommandRequestSchema.extend({
	nodeExecutionId: IdSchema,
});
export type RetryNodeRequest = z.infer<typeof RetryNodeRequestSchema>;

export const ExecutionTimelineSchema = z.object({
	execution: WorkflowExecutionSchema,
	stages: z.array(StageExecutionSchema),
	nodes: z.array(NodeExecutionSchema),
	attempts: z.array(NodeAttemptSchema),
	subtasks: z.array(DynamicSubtaskSchema),
	gates: z.array(GateEvaluationSchema),
	waitHandles: z.array(WaitHandleSchema),
	effects: z.array(EffectRecordSchema),
	checkpoints: z.array(CheckpointSchema),
	transitions: z.array(ExecutionTransitionSchema),
	providerInvocationIds: z.array(IdSchema),
	artifactIds: z.array(IdSchema),
});
export type ExecutionTimeline = z.infer<typeof ExecutionTimelineSchema>;

export const WorkflowDefinitionPageSchema = pageSchema(WorkflowDefinitionSchema);
export const WorkflowExecutionPageSchema = pageSchema(WorkflowExecutionSchema);
export const DynamicSubtaskPageSchema = pageSchema(DynamicSubtaskSchema);
export const WaitHandlePageSchema = pageSchema(WaitHandleSchema);

/** Create a new DRAFT version for a workflow key (version = max+1). */
export const CreateDefinitionRequestSchema = z.object({
	key: z.string().min(1),
	displayName: z.string().min(1),
	stages: z.array(WorkflowStageDefinitionSchema).min(1),
	nodes: z.array(WorkflowNodeDefinitionSchema).min(1),
	workflowDeadlineMs: z.number().int().min(1000).optional(),
});
export type CreateDefinitionRequest = z.infer<typeof CreateDefinitionRequestSchema>;

/** Overwrite a DRAFT definition in place. PUBLISHED rows are immutable. */
export const UpdateDefinitionRequestSchema = z.object({
	displayName: z.string().min(1),
	stages: z.array(WorkflowStageDefinitionSchema).min(1),
	nodes: z.array(WorkflowNodeDefinitionSchema).min(1),
	workflowDeadlineMs: z.number().int().min(1000).optional(),
});
export type UpdateDefinitionRequest = z.infer<typeof UpdateDefinitionRequestSchema>;

// ---------------------------------------------------------------------------
// Workflow LLM authoring — conversational DRAFT generation
// ---------------------------------------------------------------------------


export const WorkflowAuthoringSessionStatusSchema = z.enum(["ACTIVE", "CLOSED"]);
export type WorkflowAuthoringSessionStatus = z.infer<typeof WorkflowAuthoringSessionStatusSchema>;

export const WorkflowAuthoringSessionSchema = z.object({
	id: IdSchema,
	tenantId: IdSchema,
	projectId: IdSchema,
	/** DRAFT definition being edited; null until first apply creates one. */
	definitionId: IdSchema.nullable(),
	workflowKey: z.string().min(1),
	providerId: IdSchema,
	modelId: IdSchema,
	status: WorkflowAuthoringSessionStatusSchema,
	createdAt: IsoDateTimeSchema,
	updatedAt: IsoDateTimeSchema,
});
export type WorkflowAuthoringSession = z.infer<typeof WorkflowAuthoringSessionSchema>;

export const WorkflowAuthoringMessageRoleSchema = z.enum(["USER", "ASSISTANT", "SYSTEM"]);
export type WorkflowAuthoringMessageRole = z.infer<typeof WorkflowAuthoringMessageRoleSchema>;

export const WorkflowAuthoringMessageSchema = z.object({
	id: IdSchema,
	sessionId: IdSchema,
	role: WorkflowAuthoringMessageRoleSchema,
	contents: z.array(ContentPartSchema).min(1),
	/** Full Workflow definition proposal from the assistant (stages/nodes). */
	proposedDefinition: z
		.object({
			displayName: z.string().min(1),
			stages: z.array(WorkflowStageDefinitionSchema).min(1),
			nodes: z.array(WorkflowNodeDefinitionSchema).min(1),
			workflowDeadlineMs: z.number().int().min(1000).optional(),
		})
		.nullable()
		.default(null),
	createdAt: IsoDateTimeSchema,
});
export type WorkflowAuthoringMessage = z.infer<typeof WorkflowAuthoringMessageSchema>;

export const CreateWorkflowAuthoringSessionRequestSchema = z.object({
	workflowKey: z.string().min(1),
	definitionId: IdSchema.nullable().default(null),
	providerId: IdSchema,
	modelId: IdSchema,
});
export type CreateWorkflowAuthoringSessionRequest = z.infer<
	typeof CreateWorkflowAuthoringSessionRequestSchema
>;

export const UpdateWorkflowAuthoringSessionRequestSchema = z.object({
	providerId: IdSchema.optional(),
	modelId: IdSchema.optional(),
	status: WorkflowAuthoringSessionStatusSchema.optional(),
});
export type UpdateWorkflowAuthoringSessionRequest = z.infer<
	typeof UpdateWorkflowAuthoringSessionRequestSchema
>;

export const PostWorkflowAuthoringMessageRequestSchema = z.object({
	contents: z.array(ContentPartSchema).min(1),
	webSearch: z.boolean().default(false),
});
export type PostWorkflowAuthoringMessageRequest = z.infer<
	typeof PostWorkflowAuthoringMessageRequestSchema
>;

export const PostWorkflowAuthoringMessageResponseSchema = z.object({
	userMessage: WorkflowAuthoringMessageSchema,
	assistantMessage: WorkflowAuthoringMessageSchema,
});
export type PostWorkflowAuthoringMessageResponse = z.infer<
	typeof PostWorkflowAuthoringMessageResponseSchema
>;

export const ApplyWorkflowAuthoringProposalRequestSchema = z.object({
	/** When omitted, applies the latest assistant proposedDefinition. */
	messageId: IdSchema.nullable().default(null),
});
export type ApplyWorkflowAuthoringProposalRequest = z.infer<
	typeof ApplyWorkflowAuthoringProposalRequestSchema
>;

export const ApplyWorkflowAuthoringProposalResponseSchema = z.object({
	definition: WorkflowDefinitionSchema,
	session: WorkflowAuthoringSessionSchema,
});
export type ApplyWorkflowAuthoringProposalResponse = z.infer<
	typeof ApplyWorkflowAuthoringProposalResponseSchema
>;

export const WorkflowAuthoringSessionPageSchema = pageSchema(WorkflowAuthoringSessionSchema);
export const WorkflowAuthoringMessagePageSchema = pageSchema(WorkflowAuthoringMessageSchema);
